import { Box, SxProps, Theme, Typography } from '@mui/material';
import { getShortString } from 'lib/functions/utils';
import React from 'react';

import { LightTooltip } from '../../../../../../lib/designSystem/WhiteTooltip';

export interface SummaryRowProps {
  label: string;
  value: string | React.ReactNode;
  labelVariant?: 'body1' | 'body2' | 'caption';
  valueVariant?: 'body1' | 'body2' | 'caption';
  labelColor?: string;
  valueColor?: string;
  labelWeight?: number;
  showBorder?: boolean;
  valueMaxLength?: number;
  sx?: SxProps<Theme>;
}

const SummaryRow: React.FC<SummaryRowProps> = ({
  label,
  value,
  labelVariant = 'body2',
  valueVariant = 'body2',
  labelColor = 'text.secondary',
  valueColor = 'text.primary',
  labelWeight = 600,
  showBorder = true,
  valueMaxLength,
  sx,
}) => {
  const renderValue = () => {
    if (valueMaxLength && typeof value === 'string' && value) {
      const truncatedValue = getShortString(value, valueMaxLength);
      const needsTooltip = value.length > valueMaxLength;

      if (needsTooltip) {
        return (
          <LightTooltip title={<Typography padding={1}>{value}</Typography>}>
            <Typography
              variant={valueVariant}
              color={valueColor}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {truncatedValue}
            </Typography>
          </LightTooltip>
        );
      }
    }

    return (
      <Typography variant={valueVariant} color={valueColor}>
        {value}
      </Typography>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 0.75,
        ...(showBorder && {
          borderBottom: '1px solid',
          borderColor: 'divider',
        }),
        '&:last-child': {
          borderBottom: 'none',
        },
        ...sx,
      }}
    >
      <Typography
        fontWeight={labelWeight}
        variant={labelVariant}
        color={labelColor}
      >
        {label}
      </Typography>
      {renderValue()}
    </Box>
  );
};

export default SummaryRow;
